import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth, axiosInstance as axios } from '../context/AuthContext';
import { FaCheckCircle, FaCircle, FaTimes, FaQuestionCircle, FaLightbulb } from 'react-icons/fa';

const API = '/api';

// Onboarding Checklist Component
export function OnboardingChecklist({ onClose }) {
  const [onboarding, setOnboarding] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchOnboarding();
  }, []);

  const fetchOnboarding = async () => {
    try {
      const response = await axios.get(`${API}/onboarding`, { withCredentials: true });
      setOnboarding(response.data);
    } catch (error) {
      console.error('Error fetching onboarding:', error);
    } finally {
      setLoading(false);
    }
  };

  const steps = [
    { 
      id: 'create_family', 
      label: 'Create your first family', 
      description: 'Start by creating a family group',
      howTo: 'Click "Create Family" on the Dashboard'
    },
    { 
      id: 'add_members', 
      label: 'Invite family members', 
      description: 'Add members by email to your family',
      howTo: 'Go to your family → Members tab → "Add Member"'
    },
    { 
      id: 'create_event', 
      label: 'Create an event', 
      description: 'Plan your first family gathering',
      howTo: 'Go to your family → Events tab → "Create Event"'
    },
    { 
      id: 'setup_dues', 
      label: 'Set up family dues', 
      description: 'Configure dues for your family (Admin)',
      howTo: 'Go to your family → Dues tab → "Configure Dues"'
    },
    { 
      id: 'upload_photo', 
      label: 'Share a memory', 
      description: 'Upload your first photo',
      howTo: 'Go to an event → Photos tab → Upload a photo'
    },
  ];

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-lg p-6 max-w-md">
        <div className="animate-pulse">Loading...</div>
      </div>
    );
  }

  const completedSteps = onboarding?.completed_steps || [];
  const progress = (completedSteps.length / steps.length) * 100;

  return (
    <div className="bg-white rounded-xl shadow-lg p-6 max-w-md relative" data-testid="onboarding-checklist">
      {/* Close button - prominent with gray background */}
      <button 
        onClick={onClose}
        style={{
          position: 'absolute',
          top: '12px',
          right: '12px',
          width: '36px',
          height: '36px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: '50%',
          backgroundColor: '#e5e7eb',
          border: 'none',
          cursor: 'pointer',
          zIndex: 10
        }}
        className="hover:bg-gray-300 text-gray-600 hover:text-gray-800 transition-colors"
        data-testid="close-checklist"
        aria-label="Close checklist"
      >
        <FaTimes style={{ fontSize: '16px' }} />
      </button>
      
      <div className="pr-10">
        <h3 className="text-xl font-bold text-gray-800">Getting Started</h3>
        <p className="text-sm text-gray-600">Complete these steps to get the most out of your family platform</p>
      </div>

      {/* Progress Bar */}
      <div className="mt-4 mb-6">
        <div className="flex justify-between text-sm mb-1">
          <span className="text-gray-600">Progress</span>
          <span className="font-medium text-blue-600">{completedSteps.length}/{steps.length}</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div 
            className="bg-blue-600 h-2 rounded-full transition-all duration-500"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* Steps */}
      <div className="space-y-3">
        {steps.map((step) => {
          const isCompleted = completedSteps.includes(step.id);
          return (
            <div 
              key={step.id}
              className={`flex items-start space-x-3 p-3 rounded-lg ${
                isCompleted ? 'bg-green-50' : 'bg-gray-50'
              }`}
            >
              {isCompleted ? (
                <FaCheckCircle className="text-green-600 mt-0.5 flex-shrink-0" />
              ) : (
                <FaCircle className="text-gray-300 mt-0.5 flex-shrink-0" />
              )}
              <div className="flex-1">
                <p className={`font-medium ${isCompleted ? 'text-green-800 line-through' : 'text-gray-800'}`}>
                  {step.label}
                </p>
                <p className="text-sm text-gray-600">{step.description}</p>
                {!isCompleted && step.howTo && (
                  <p className="text-xs text-blue-600 mt-1 flex items-center">
                    <FaLightbulb className="mr-1" />
                    {step.howTo}
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {completedSteps.length === steps.length && (
        <div className="mt-4 p-4 bg-green-100 rounded-lg text-center">
          <p className="text-green-800 font-medium">🎉 Congratulations! You've completed all steps!</p>
        </div>
      )}
      
      {/* Subtle link to milestones */}
      <div className="mt-4 pt-4 border-t border-gray-200">
        <Link 
          to="/achievements"
          onClick={onClose}
          className="text-sm text-gray-500 hover:text-blue-600 flex items-center justify-center gap-1"
          data-testid="view-milestones-link"
        >
          <span>🏆</span>
          <span>View Family Milestones</span>
        </Link>
      </div>
    </div>
  );
}

// Tooltip Component for Tutorial Hints
export function TutorialTooltip({ children, tip, position = 'top' }) {
  const [showTip, setShowTip] = useState(false);

  const positionClasses = {
    top: 'bottom-full left-1/2 -translate-x-1/2 mb-2',
    bottom: 'top-full left-1/2 -translate-x-1/2 mt-2',
    left: 'right-full top-1/2 -translate-y-1/2 mr-2',
    right: 'left-full top-1/2 -translate-y-1/2 ml-2',
  };

  return (
    <div className="relative inline-block">
      <div
        onMouseEnter={() => setShowTip(true)}
        onMouseLeave={() => setShowTip(false)}
      >
        {children}
      </div>
      {showTip && (
        <div className={`absolute z-50 ${positionClasses[position]} w-64 p-3 bg-gray-800 text-white text-sm rounded-lg shadow-lg`}>
          <div className="flex items-start space-x-2">
            <FaLightbulb className="text-yellow-400 flex-shrink-0 mt-0.5" />
            <span>{tip}</span>
          </div>
        </div>
      )}
    </div>
  );
}

// Help Button Component - "Getting Started" button
export function HelpButton({ onClick }) {
  return (
    <button
      onClick={onClick}
      className="fixed bottom-20 left-6 flex items-center space-x-2 bg-blue-600 text-white px-4 py-3 rounded-full shadow-lg hover:bg-blue-700 transition-colors z-40"
      data-testid="help-button"
      title="Getting Started Guide"
    >
      <FaLightbulb className="text-lg" />
      <span className="font-medium">Getting Started</span>
    </button>
  );
}

// Tutorial component - simplified without Joyride
export function Tutorial({ context = 'dashboard', run = false, onComplete }) {
  // This is a placeholder - the actual tutorial now uses the OnboardingChecklist
  useEffect(() => {
    if (run && onComplete) {
      onComplete();
    }
  }, [run, onComplete]);

  return null;
}

export default OnboardingChecklist;
